// @ts-strict-ignore
import { ArticleContainer } from '../article-container';
import { MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { Connection } from '../connection';
import { Material, Matrix4, Mesh, PlaneGeometry, Vector3 } from 'three';
import { MeshUtils } from '../../common/util/mesh-utils';
import { MetricUtils } from '../../common/util/metric-utils';
import { MathUtils } from '../../common/util/math-utils';
import { FaceUtils } from '../../common/util/face-utils';
import { SpriteMaterial } from '../../material/sprite-material';
import { TextureUtils } from './texture-utils';
import { Object3DUtils } from '../../common/util/object-3d-utils';
import { ConnectionFactory } from '../connection.factory';
import { Injectable } from '@angular/core';
import { Rectangle3D } from '../../model/rectangle-3d';
import { CommonConstants } from '../../constants/common-constants';
import { ContainerService } from '../container.service';

@Injectable({ providedIn: 'root' })
export class MeshComponentUtils {

  constructor(private _componentUtils: ContainerService,
              private _connectionFactory: ConnectionFactory) {
  }

  public addGivingMeshToContainer(
    givingContainer: ArticleContainer,
    acceptingAreaId: MetaElementUsageIdentityVO,
    connectionName: string,
    isInvertedDirection: boolean
  ): Connection {
    const acceptingContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(acceptingAreaId.articleUsageId);
    const acceptingConnection: Connection = acceptingContainer.findConnectionByName(acceptingAreaId.metaElementName);
    let givingConnection: Connection = null;
    if (acceptingConnection) {
      // create connection mesh for giving container by using the same as the accepting connection
      const givingMesh: Mesh = MeshUtils.cloneMesh(acceptingConnection.mesh);
      givingMesh.name = connectionName;

      if (isInvertedDirection) {
        const givingMeshOriginMiddlePoint: Vector3 = MetricUtils.getMiddlePoint(givingMesh);
        const givingMeshTransform: Matrix4 = new Matrix4();
        const axis: Vector3 = MathUtils.getNormalFromFirstPoint(
          acceptingConnection.localRectangle.point1,
          acceptingConnection.localRectangle.point2,
          acceptingConnection.localRectangle.point3
        );
        givingMeshTransform.makeRotationAxis(axis, MathUtils.toRadians(180));
        FaceUtils.transformMesh(givingMesh, givingMeshTransform, true);
        // Rotation axis is not at the center of the mesh, so we have to move it back.
        const givingMeshCurrentMiddlePoint: Vector3 = MetricUtils.getMiddlePoint(givingMesh);
        const offset: Vector3 = new Vector3().subVectors(givingMeshOriginMiddlePoint, givingMeshCurrentMiddlePoint);
        FaceUtils.moveMeshByOffset(givingMesh, offset);
      }
      acceptingContainer.updateMatrixWorld(true);
      givingContainer.updateMatrixWorld(true);
      FaceUtils.moveMeshBetweenContainers(givingMesh, acceptingContainer, givingContainer);
      FaceUtils.inverseUVDirection(givingMesh);

      // add created meshes to giving container
      givingContainer.addChildToModel(givingMesh);
      givingConnection = this._connectionFactory.getNewInstance(givingContainer, givingMesh);
      givingContainer.setConnection(givingConnection);
    }
    return givingConnection;
  }

  public addTextMeshToContainer(
    givingContainer: ArticleContainer,
    givingAreaConnection: Connection,
    connectionName: string,
    offset: Vector3,
    connectionWidth: number,
    createTexture: boolean = true
  ): void {
    const spriteMaterial: SpriteMaterial = TextureUtils.createTextMaterial(givingAreaConnection.mesh);
    const textMesh: Mesh = new Mesh(new PlaneGeometry(1, 1, 1, 1), spriteMaterial);
    // Make text mesh material invisible, because labeling textures are generated at server side and in case when strip is merged
    // unused texture connections are visible (gray color)
    (textMesh.material as Material).visible = false;
    textMesh.name = connectionName;
    const clonedGivingMesh: Mesh = MeshUtils.cloneMesh(givingAreaConnection.mesh);
    FaceUtils.inverseUVDirection(clonedGivingMesh);

    givingContainer.addChildToModel(textMesh);
    this.rotateMesh(textMesh, clonedGivingMesh);
    this.moveMesh(textMesh, clonedGivingMesh);
    this.resizeTextureMeshByParameters(textMesh, givingContainer, offset, connectionWidth);
    if (createTexture) {
      TextureUtils.createTexture(textMesh);
    }
    Object3DUtils.destroy(clonedGivingMesh);

    const textureAreaConnection: Connection = this._connectionFactory.getNewInstance(givingContainer, textMesh, Connection.TYPE_TEXT);
    givingContainer.setConnection(textureAreaConnection);
  }

  private rotateMesh(mesh: Mesh, targetMesh: Mesh): void {
    const meshBoundingRect: Rectangle3D = MathUtils.calculateBoundingUVRectangle(mesh.geometry);
    const targetBoundingRect: Rectangle3D = MathUtils.calculateBoundingUVRectangle(targetMesh.geometry);

    const meshFace: Vector3[] = meshBoundingRect.toFace();
    const targetFace: Vector3[] = targetBoundingRect.toFace();
    const rotationMatrix: Matrix4 = MathUtils.getRotationMatrix(meshFace, targetFace, false);

    FaceUtils.transformMesh(mesh, rotationMatrix, true);
  }

  private moveMesh(mesh: Mesh, targetMesh: Mesh): void {
    const middlePointFromMeshGeometry: Vector3 = MetricUtils.getMiddlePoint(mesh);
    const middlePointFromTargetGeometry: Vector3 = MetricUtils.getMiddlePoint(targetMesh);
    const offset: Vector3 = middlePointFromTargetGeometry.clone().sub(middlePointFromMeshGeometry);

    FaceUtils.moveMeshByOffset(mesh, offset);
  }

  private resizeTextureMeshByParameters(textureMesh: Mesh, givingContainer: ArticleContainer, offset: Vector3, connectionWidth: number): void {
    const connectionNames: string[] = [];
    for (const connection of givingContainer.connections) {
      // collect connection names to exclude connections from real size of giving article
      connectionNames.push(connection.id);
    }

    const realSizeInLocal: Vector3 = MetricUtils.getRealSize(givingContainer.model, connectionNames);
    const articleThickness: number = realSizeInLocal.y + 2 * CommonConstants.EPSILON;
    const articleHeight: number = realSizeInLocal.z;
    const connectionRectangle: Rectangle3D = MathUtils.calculateBoundingUVRectangle(textureMesh.geometry);
    const ratioWidth: number = connectionWidth / connectionRectangle.width;
    const ratioHeight: number = articleHeight / connectionRectangle.height;

    const scaleMatrix: Matrix4 = MathUtils.constructBasicScaleMatrix(
      connectionRectangle.point1,
      connectionRectangle.point4,
      MathUtils.getInterpolated(connectionRectangle.point4, connectionRectangle.point1, ratioWidth),
      true
    );
    scaleMatrix.multiply(
      MathUtils.constructBasicScaleMatrix(
        connectionRectangle.point1,
        connectionRectangle.point2,
        MathUtils.getInterpolated(connectionRectangle.point2, connectionRectangle.point1, ratioHeight),
        true
      )
    );
    FaceUtils.scaleMesh(textureMesh, MetricUtils.getMiddlePoint(textureMesh), scaleMatrix);

    const textureRectangle: Rectangle3D = MathUtils.calculateBoundingUVRectangle(textureMesh.geometry);
    const textConnector: Vector3 = MathUtils.calculateConnectorFromFace(textureMesh.geometry, textureRectangle.point1, articleThickness);
    const textOffset: Vector3 = textConnector.clone().sub(textureRectangle.point1);
    FaceUtils.moveMeshByOffset(textureMesh, textOffset);

    // transfer offset in local space to apply it to (local) mesh vertices
    const localStart: Vector3 = givingContainer.worldToLocal(new Vector3());
    const localOffset: Vector3 = givingContainer.worldToLocal(offset.clone());
    const meshOffset: Vector3 = localOffset.clone().sub(localStart);

    // check offset by Y
    const previousOffset: Vector3 = this.getPreviousOffset(offset, connectionWidth);
    const localPreviousOffset: Vector3 = givingContainer.worldToLocal(previousOffset);
    const meshPreviousOffset: Vector3 = localPreviousOffset.clone().sub(localStart);
    if (MathUtils.compare(meshOffset.y, meshPreviousOffset.y) === 0) {
      meshOffset.y = 0;
    }

    FaceUtils.moveMeshByOffset(textureMesh, meshOffset);
  }

  private getPreviousOffset(offset: Vector3, width: number): Vector3 {
    if (offset.length() === 0 || width === 0) {
      return offset;
    }
    const resultOffset: Vector3 = offset.clone();
    if (MathUtils.compare(resultOffset.x, 0) !== 0) {
      resultOffset.x = resultOffset.x - width;
    } else if (MathUtils.compare(resultOffset.z, 0) !== 0) {
      resultOffset.z = resultOffset.z - width;
    } else if (MathUtils.compare(resultOffset.y, 0) !== 0) {
      resultOffset.y = resultOffset.y - width;
    }
    return resultOffset;
  }
}
